/**
 * Google OAuth — redirect first, manual popup as the safety net.
 *
 * Happy path (every browser): `signInWithRedirect` navigates this
 * popup straight to Google's account picker. Zero extra clicks, and
 * no Horus UI beyond the moment it takes to load.
 *
 * Fallback: a "Continue with Google" button (auto-focused, Enter
 * works) whose click runs `signInWithPopup` in a nested window. It
 * appears only when the redirect could not carry the flow — the 4s
 * bailout, the post-redirect loop guard, or iframe mode, where a
 * cross-origin frame has no user activation at load and the click is
 * what grants it.
 *
 * This used to be a browser fork, with Brave routed to the button
 * unconditionally: the OAuth relay lived on `<project>.firebaseapp.com`,
 * so its storage was THIRD-PARTY to this page and Brave's Shields made
 * the redirect helper unreadable. `resolveAuthDomain()` now points
 * `authDomain` at this page's own hostname and CloudFront serves
 * `/__/*` from here, so the relay is first-party and there is nothing
 * left for Shields to block. Brave takes the redirect like everyone
 * else, and the browser sniffing is gone.
 *
 * The DOM listeners (popupBtn click, cancel) are attached BEFORE any
 * async work, so the button is live on every path that can reach it.
 * An earlier iteration wired them at the end of the function, which
 * left the fallback button inert and broke sign-in outright.
 */

import {
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
} from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';
import { PostMessageContext } from '../postmessage';
import {
  el,
  mount,
  postCancel,
  postError,
  postReady,
  postSuccess,
  humanizeFirebaseError,
  setBusy,
  getQueryParam,
} from '../util';

const REDIRECT_TIMEOUT_MS = 4000;
const REDIRECT_ATTEMPTED_KEY = 'horus.auth.google.redirectAttempted';


export async function renderGoogleFlow(ctx: PostMessageContext): Promise<void> {
  // ── DOM setup ─────────────────────────────────────────────────────
  const status = el('p', { class: 'subtitle' }, ['Continuing with Google…']);
  const errorBox = el('p', {
    'data-error': 'true',
    class: 'error',
    style: 'display:none',
  });
  const popupBtn = el(
    'button',
    {
      class: 'primary',
      type: 'button',
      'data-testid': 'google-continue',
      style: 'display:none;margin-top:8px;',
    },
    ['Continue with Google']
  ) as HTMLButtonElement;
  const cancel = el('button', { type: 'button' }, ['Cancel']);

  const panel = el('div', {}, [
    el('h1', {}, ['Sign in']),
    status,
    errorBox,
    popupBtn,
    cancel,
  ]);

  // Deliberately NOT mounted yet.
  //
  // The happy path redirects to Google without ever rendering, so
  // mounting here would flash a Horus "Sign in" card in front of the
  // account chooser -- the thing partners keep asking us to remove.
  // Every path that needs UI (the fallback button, an error, iframe
  // mode) calls `ensureMounted()` first, so nothing becomes
  // unreachable.
  //
  // The nodes and their listeners are still built EAGERLY, above and
  // below: a past bug wired the button only at the end of the
  // function, leaving the fallback inert on the paths that returned
  // early. Construction order is the fix for that, not mount order.
  let mounted = false;
  function ensureMounted(): void {
    if (mounted) return;
    mounted = true;
    mount(panel);
  }

  postReady(ctx);

  const auth = getFirebaseAuth();

  // ── Helpers (closures over the DOM above; safe to reference from
  //    listeners attached below) ────────────────────────────────────
  function showPopupFallback(message: string): void {
    ensureMounted();
    status.textContent = message;
    popupBtn.style.display = '';
    popupBtn.focus();
  }

  function surfaceFirebaseError(err: unknown): void {
    ensureMounted();
    const { code, message } = humanizeFirebaseError(err);
    status.textContent = '';
    errorBox.textContent = message;
    errorBox.style.display = 'block';
    popupBtn.style.display = 'none';
    cancel.textContent = 'Close';
    postError(ctx, code, message);
  }

  async function runPopupSignIn(): Promise<void> {
    errorBox.style.display = 'none';
    popupBtn.disabled = true;
    setBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await postSuccess(ctx, result.user, 'google.com');
    } catch (err) {
      popupBtn.disabled = false;
      surfaceFirebaseError(err);
      // Most popup failures are retryable (popup blocked, user closed
      // Google's window, transient network blip). surfaceFirebaseError
      // hides the button + flips Cancel to Close for generic terminal
      // errors; override that here so the user can click again. The
      // Cancel button label stays as whatever surface… set it to —
      // we want retry to be the obvious next action, not closing.
      popupBtn.style.display = '';
      cancel.textContent = 'Cancel';
    } finally {
      setBusy(false);
    }
  }

  /**
   * Start the nested-popup sign-in immediately, WITHOUT rendering the
   * "Continue with Google" button first.
   *
   * Used on every path where the user has already made a deliberate
   * Google choice upstream — the SDK's `loginWithGoogle()`, or the
   * unified chooser's Google tile. Showing a "Continue with Google"
   * button there is a duplicate of the button they just clicked,
   * which is exactly what a partner running their own login UI is
   * trying to get rid of.
   *
   * Popup mode only. The popup window inherits user activation from
   * the `window.open()` that created it (itself a response to the
   * partner-side click), so the nested OAuth window isn't blocked.
   * If it is blocked anyway, `runPopupSignIn`'s catch reveals the
   * button — so the worst case degrades to the old one-click
   * behavior rather than a dead end.
   */
  function autoStartSignIn(message: string): void {
    ensureMounted();
    status.textContent = message;
    void runPopupSignIn();
  }

  // ── Listeners attached BEFORE any early return ────────────────────
  // Critical: every code path below may `return` before the bottom of
  // the function. Wiring the button here guarantees it's clickable in
  // every path (Brave fallback, loop-guard fallback, redirect-timeout
  // fallback, etc.). Past bug: handlers were at the bottom and the
  // Brave path showed an inert button.
  cancel.addEventListener('click', () => postCancel(ctx));
  popupBtn.addEventListener('click', () => {
    void runPopupSignIn();
  });

  // ── Phase 2: post-redirect return ─────────────────────────────────
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
      await postSuccess(ctx, result.user, 'google.com');
      return;
    }
  } catch (err) {
    window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
    surfaceFirebaseError(err);
    return;
  }

  // ── Iframe mode: always require an in-iframe click ────────────────
  // The iframe is cross-origin to the partner page. The user gesture
  // from "Open … in modal" lives in the PARENT context and does not
  // propagate into the iframe at load time. Without user activation
  // inside the iframe, Brave / Chrome-with-third-party-blocking refuse
  // to open the OAuth popup — symptom is `auth/popup-blocked` thrown
  // by `signInWithPopup` because window.open returns null.
  //
  // So in iframe mode we ALWAYS render the manual "Continue with
  // Google" button and wait for the user to click it. That click
  // happens inside the iframe → iframe gets user activation →
  // signInWithPopup is allowed.
  //
  // This also avoids attempting `signInWithRedirect`, which can't
  // work in an iframe anyway (Google's OAuth page sets
  // `X-Frame-Options: DENY` and refuses to render).
  //
  // `autostart=1` is intentionally ignored here for the same reason —
  // it's only useful in popup mode where the popup window inherits
  // user activation from the click that opened it.
  const isIframe = getQueryParam('mode') === 'iframe';
  if (isIframe) {
    showPopupFallback('Continue with Google to sign in.');
    return;
  }

  // ── Popup mode: autostart skips the redundant second click ────────
  // Set by the unified chooser (and any future caller that already
  // captured a deliberate Google choice from the user). The user just
  // clicked "Continue with Google" upstream — we'd be making them
  // click the same button again if we showed the fallback UI here.
  //
  // Safe in popup mode because the popup window inherits user
  // activation from `window.open()` (called in response to the
  // partner-side click), so `signInWithPopup`'s nested OAuth popup
  // isn't blocked. If something does block it, `runPopupSignIn` keeps
  // the fallback button visible so the user can retry with one click.
  const autostart = getQueryParam('autostart') === '1';
  if (autostart) {
    autoStartSignIn('Opening Google sign-in…');
    return;
  }

  // ── Loop guard: redirect happened but result couldn't be read ─────
  const alreadyAttemptedRedirect =
    window.sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY) === '1';
  if (alreadyAttemptedRedirect) {
    window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
    showPopupFallback(
      "Your browser's privacy settings blocked the standard sign-in path. Click Continue to sign in."
    );
    return;
  }

  // ── Brave ────────────────────────────────────────────────────────
  // There is deliberately no Brave short-circuit here any more.
  //
  // It existed because the OAuth relay lived on
  // `<project>.firebaseapp.com`, making its storage THIRD-PARTY to this
  // page -- which Brave's Shields block, leaving the redirect helper
  // unreadable. The workaround was a nested popup, and that needs a
  // click inside this window, which is the "second step" partners kept
  // reporting. (Auto-starting it instead does not work: the popup does
  // not inherit user activation on this path, so signInWithPopup throws
  // `auth/popup-blocked` and the user gets an error before the same
  // click. Tried 2026-08-31; strictly worse.)
  //
  // `resolveAuthDomain()` now points `authDomain` at this page's own
  // hostname, and CloudFront serves `/__/*` from here, so the relay is
  // first-party and Brave has nothing to block. Brave therefore takes
  // the ordinary redirect below like every other browser.
  //
  // If that assumption is ever wrong, this degrades rather than breaks:
  // the 4s bailout timer shows the manual button, and the
  // REDIRECT_ATTEMPTED_KEY loop guard makes the next attempt show it
  // immediately.

  // ── Phase 1: redirect on browsers that support it ────────────────
  let bailedOut = false;
  const bailoutTimer = setTimeout(() => {
    bailedOut = true;
    window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
    showPopupFallback(
      'The auto sign-in is taking too long. Click Continue to sign in with a popup instead.'
    );
  }, REDIRECT_TIMEOUT_MS);

  try {
    window.sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, '1');
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
    // Resolved without navigating — show fallback.
    if (!bailedOut) {
      clearTimeout(bailoutTimer);
      bailedOut = true;
      window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
      showPopupFallback('Click Continue to sign in.');
    }
  } catch (err) {
    clearTimeout(bailoutTimer);
    window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
    surfaceFirebaseError(err);
  }
}
